import React, { useRef, useMemo } from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import _ from 'lodash';
import PropTypes from 'prop-types';

import ServicesList from '../../../components/List';

import * as importTypes from '../../../reducer/constants';
import { getHighlightingText, getProvider } from '../../../../../../helpers/getters';
import { splitToRows } from '../../1/helper';

const Services = ({ listSelected, isLoadingRate, providerId, dispatchImportState, search }) => {
  const listRef = useRef();

  const cache = new CellMeasurerCache({
    defaultHeight: 77,
    minHeight: 56,
    fixedWidth: true,
  });

  const provider = useMemo(() => getProvider(providerId), [providerId]);
  const list = useMemo(() => splitToRows(listSelected), [listSelected]);

  const isSyncRate = provider?.service_auto_rate;

  const onChangeRate = (categoryIndex, serviceIndex, value) => {
    const cloneList = _.cloneDeep(listSelected);
    if (value === '') {
      cloneList[categoryIndex].services[serviceIndex].rate = null;
      dispatchImportState({ type: importTypes.IMPORT_CHANGE_RATE, list: cloneList });
    } else {
      if (/^[0-9]+(?:\.|\.[0-9]{0,4})?$/.test(value)) {
        cloneList[categoryIndex].services[serviceIndex].rate = value;
        dispatchImportState({ type: importTypes.IMPORT_CHANGE_RATE, list: cloneList });
      }
    }
  };

  const onBlurRate = (categoryIndex, serviceIndex, value) => {
    const cloneList = _.cloneDeep(listSelected);
    if (value === '' || value === '0') {
      cloneList[categoryIndex].services[serviceIndex].rate = '0.01';
      dispatchImportState({ type: importTypes.IMPORT_CHANGE_RATE, list: cloneList });
    }
  };

  const toggleEdit = (id, categoryIndex, index) => {
    dispatchImportState({ type: importTypes.IMPORT_TOGGLE_EDIT_SERVICE, id, categoryIndex });
    listRef?.current?.recomputeRowHeights(index);
    listRef?.current?.forceUpdateGrid();
  };

  const onSaveService = (id, categoryIndex, serviceIndex, index) => {
    const cloneList = _.cloneDeep(listSelected);
    const serviceClone = cloneList[categoryIndex].services[serviceIndex];
    cloneList[categoryIndex].services[serviceIndex] = {
      ...serviceClone,
      ...serviceClone.formData,
    };
    dispatchImportState({ type: importTypes.IMPORT_SAVE_SERVICE_CHANGES, list: cloneList });
    toggleEdit(id, categoryIndex, index);
  };

  const onChangeServiceForm = (event, categoryIndex, serviceIndex) => {
    const cloneList = _.cloneDeep(listSelected);
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    cloneList[categoryIndex].services[serviceIndex].formData[event.target.name] = value;
    dispatchImportState({ type: importTypes.IMPORT_CHANGE_SERVICE_FORM, list: cloneList });
  };

  if (listSelected) {
    return (
      <ServicesList.Wrapper>
        <AutoSizer disableHeight>
          {({ width }) => (
            <List
              ref={listRef}
              width={width}
              height={654}
              overscanRowCount={20}
              rowCount={list.length}
              deferredMeasurementCache={cache}
              rowHeight={cache.rowHeight}
              rowRenderer={({ index, style, key, parent }) => {
                const category = list[index];

                switch (category.collection) {
                case 'category':
                  return (
                    <CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
                      <ServicesList.Category style={style} key={index}>
                        <ServicesList.Header>
                          <ServicesList.CategoryAssign category={category} />
                        </ServicesList.Header>
                      </ServicesList.Category>
                    </CellMeasurer>
                  );

                default:
                  const {
                    id,
                    name,
                    min,
                    max,
                    formData,
                    rate,
                    rateCopy,
                    disabled,
                    cancel,
                    refill,
                    dripfeed,
                    categoryIndex,
                    serviceIndex,
                    provider_rate,
                    locked,
                    showEdit,
                  } = category;
                  const nameWithId = `${id} - ${name}`;

                  return (
                    <CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
                      <ServicesList.Row disabled={disabled} style={style} key={id}>
                        <ServicesList.ServiceCol>
                          <ServicesList.ServiceInfo>
                            <ServicesList.ServiceData>
                              <ServicesList.Name>
                                <span dangerouslySetInnerHTML={getHighlightingText(nameWithId, search)} />
                              </ServicesList.Name>
                              <ServicesList.Right>
                                {rateCopy ? (
                                  <ServicesList.CustomRate
                                    isLoading={isLoadingRate}
                                    locked={locked}
                                    onChange={(e) => onChangeRate(categoryIndex, serviceIndex, e.target.value)}
                                    onBlur={(e) => onBlurRate(categoryIndex, serviceIndex, e.target.value)}
                                    rate={rate}
                                    sync={isSyncRate}
                                  />
                                ) : null}
                                {rateCopy ? <ServicesList.Rate provider={provider} providerRate={provider_rate} rate={rateCopy} /> : null}
                              </ServicesList.Right>
                            </ServicesList.ServiceData>
                            <ServicesList.ServiceEdit
                              options={{ cancel, refill, dripfeed, min, max }}
                              toggleEdit={() => toggleEdit(id, categoryIndex, index)}
                              onSaveService={() => onSaveService(id, categoryIndex, serviceIndex, index)}
                              showEdit={showEdit}
                            />
                            {showEdit && (
                              <ServicesList.ServiceDescriptionForm
                                id={id}
                                formData={formData}
                                onChange={(event) => onChangeServiceForm(event, categoryIndex, serviceIndex)}
                              />
                            )}
                          </ServicesList.ServiceInfo>
                        </ServicesList.ServiceCol>
                      </ServicesList.Row>
                    </CellMeasurer>
                  );
                }
              }}
            />
          )}
        </AutoSizer>
      </ServicesList.Wrapper>
    );
  }

  return null;
};

Services.propTypes = {
  listSelected: PropTypes.array,
  isLoadingRate: PropTypes.any,
  providerId: PropTypes.any,
  dispatchImportState: PropTypes.func,
  search: PropTypes.string,
};
export default Services;
